import { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { handleSSOCallback, claimSSOToken } from '../services/api'
import { errorText } from '../utils/errorText'

export default function SSOCallback() {
  const navigate = useNavigate()
  const { login: setAuth } = useAuthStore()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(true)

  useEffect(() => {
    const errorParam = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')

    if (errorParam) {
      const description = errorDescription || ''
      if (
        errorParam === 'consent_required' ||
        description.includes('AADSTS65001') ||
        description.includes('AADSTS90094')
      ) {
        setError(
          'Your organization requires an administrator to approve this application before you can ' +
          'sign in. Ask your administrator to grant admin consent from the SSO settings page.'
        )
      } else if (errorParam === 'access_denied') {
        setError('Sign-in was cancelled. You can try again or use another sign-in method.')
      } else {
        setError(description || `SSO login failed: ${errorParam}`)
      }
      setProcessing(false)
      return
    }

    // SAML flow: backend redirects here with a short-lived claim code
    const samlClaim = searchParams.get('saml_claim')
    if (samlClaim) {
      const processClaim = async () => {
        try {
          const response = await claimSSOToken(samlClaim)
          setAuth(response)
          navigate('/', { replace: true })
        } catch {
          setError('Failed to complete SAML sign-in. The link may have expired.')
          setProcessing(false)
        }
      }
      processClaim()
      return
    }

    // OIDC flow: IdP redirects here with code + state, we exchange via backend
    const code = searchParams.get('code')
    const state = searchParams.get('state')

    if (!code || !state) {
      setError('Invalid SSO callback. Missing parameters.')
      setProcessing(false)
      return
    }

    const processCallback = async () => {
      try {
        const response = await handleSSOCallback(code, state)
        // The Connect action on the SSO settings page returns a connection result, not a login
        if (response.express_connect) {
          navigate('/settings/integrations/sso?express_connected=1', { replace: true })
          return
        }
        setAuth(response)
        navigate('/', { replace: true })
      } catch (err: any) {
        setError(errorText(err, 'SSO authentication failed. Please try again.'))
        setProcessing(false)
      }
    }

    processCallback()
  }, [searchParams, setAuth, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
      <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary text-center">
        <div className="flex items-center justify-center space-x-3 mb-6">
          <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
          <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
        </div>

        {processing && !error && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
            <p className="text-theme-text-secondary">Completing SSO sign-in...</p>
          </div>
        )}

        {error && (
          <div className="space-y-4">
            <div className="w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-theme-text-primary mb-2">SSO Sign-In Failed</h3>
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
            </div>
            <Link
              to="/login"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
